import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TablePagination,
  TableHead, TableRow, Paper, IconButton, Button, Box, CircularProgress, TextField,
  Chip, Collapse, Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ObjectivesService from '../../services/ObjectivesService';
import { permissionOptions } from '../../utils/Enums';
import { getIcon } from '../../utils/IconHandler';
import AddEditObjectiveModal from './AddEditObjectiveModal';
import DeleteObjectiveModal from './DeleteObjectiveModal';
import AssignObjectiveModal from './AssignObjectiveModal';
import SnackbarUtils from '../../utils/SnackbarUtils';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';

const CATEGORY_OPTIONS = [
  { value: 'sales', label: 'Ventas' },
  { value: 'tickets', label: 'Tickets' },
  { value: 'clients', label: 'Clientes' },
  { value: 'growth', label: 'Crecimiento' }
];

const ObjectivesTable = () => {
  const { t } = useTranslation();
  const [objectives, setObjectives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [assignments, setAssignments] = useState({});

  const userPermissions = JSON.parse(localStorage.getItem('permissions')) || [];
  const hasPermission = (permissionId) => userPermissions.includes(permissionId);
  const canManage = hasPermission(permissionOptions.find(p => p.label === 'MANAGE_OBJECTIVES')?.id);

  useEffect(() => {
    loadObjectives();
  }, []);

  // Cargar asignaciones para todos los objetivos al cargar la página
  useEffect(() => {
    const loadAllAssignments = async () => {
      if (objectives.length > 0) {
        const assignmentPromises = objectives.map(async (objective) => {
          try {
            const assignmentData = await ObjectivesService.getObjectiveAssignments(objective.id);
            return { objectiveId: objective.id, assignments: assignmentData };
          } catch (error) {
            console.error(`Error loading assignments for objective ${objective.id}:`, error);
            return { objectiveId: objective.id, assignments: [] };
          }
        });

        const results = await Promise.all(assignmentPromises);
        const assignmentsMap = {};
        results.forEach(({ objectiveId, assignments }) => {
          assignmentsMap[objectiveId] = assignments;
        });
        setAssignments(assignmentsMap);
      }
    };

    loadAllAssignments();
  }, [objectives]);

  const loadObjectives = async () => {
    try {
      setLoading(true);
      const data = await ObjectivesService.getObjectives();
      setObjectives(data || []);
    } catch (error) {
      console.error('Error loading objectives:', error);
      SnackbarUtils.error(t('error_loading_objectives') || 'Error al cargar objetivos');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleAdd = () => {
    setSelectedObjective(null);
    setAddModalOpen(true);
  };

  const handleEdit = (objective) => {
    setSelectedObjective(objective);
    setEditModalOpen(true);
  };

  const handleDelete = (objective) => {
    setSelectedObjective(objective);
    setDeleteModalOpen(true);
  };

  const handleAssign = (objective) => {
    setSelectedObjective(objective);
    setAssignModalOpen(true);
  };

  const handleToggleExpand = async (objectiveId) => {
    const isExpanded = expandedRows[objectiveId];
    setExpandedRows({
      ...expandedRows,
      [objectiveId]: !isExpanded
    });

    // Cargar asignaciones si no están cargadas
    if (!isExpanded && !assignments[objectiveId]) {
      try {
        const assignmentData = await ObjectivesService.getObjectiveAssignments(objectiveId);
        setAssignments({
          ...assignments,
          [objectiveId]: assignmentData
        });
      } catch (error) {
        console.error('Error loading assignments:', error);
        // Si el endpoint no existe aún, usar array vacío
        setAssignments({
          ...assignments,
          [objectiveId]: []
        });
      }
    }
  };

  const handleSave = async () => {
    await loadObjectives();
    setAddModalOpen(false);
    setEditModalOpen(false);
    setSelectedObjective(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      await ObjectivesService.deleteObjective(selectedObjective.id);
      SnackbarUtils.success(t('objective_deleted') || 'Objetivo eliminado exitosamente');
      await loadObjectives();
      setDeleteModalOpen(false);
      setSelectedObjective(null);
    } catch (error) {
      console.error('Error deleting objective:', error);
      SnackbarUtils.error(t('error_deleting_objective') || 'Error al eliminar objetivo');
    }
  };

  const filteredObjectives = objectives.filter(obj =>
    obj.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    obj.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    obj.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedObjectives = filteredObjectives.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getCategoryLabel = (category) => {
    return CATEGORY_OPTIONS.find(opt => opt.value === category)?.label || category;
  };

  return (
    <>
      <Paper sx={{ padding: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <TextField
            label={t('search') || 'Buscar'}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: '300px' }}
          />
          {canManage && (
            <Button
              variant="contained"
              startIcon={getIcon('add')}
              onClick={handleAdd}
            >
              {t('add_objective') || 'Agregar Objetivo'}
            </Button>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="left">{t('title') || 'Título'}</TableCell>
                    <TableCell>{t('description') || 'Descripción'}</TableCell>
                    <TableCell>{t('category') || 'Categoría'}</TableCell>
                    <TableCell>{t('suggested_value') || 'Valor Sugerido'}</TableCell>
                    {canManage && <TableCell align="right">{t('actions') || 'Acciones'}</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedObjectives.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canManage ? 5 : 4} align="center">
                        {t('no_objectives') || 'No hay objetivos'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedObjectives.map((objective) => {
                      // Normalizar defaultTargetValue (camelCase del backend) a default_target_value (snake_case usado en frontend)
                      const defaultTargetValue = objective.defaultTargetValue ?? objective.default_target_value ?? objective.targetValue ?? objective.target_value ?? 0;
                      const isExpanded = expandedRows[objective.id];
                      const objectiveAssignments = assignments[objective.id] || [];
                      
                      return (
                        <React.Fragment key={objective.id}>
                          <TableRow>
                            <TableCell align="left">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {objectiveAssignments.length > 0 && (
                                  <>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleToggleExpand(objective.id)}
                                    >
                                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                    <Chip
                                      label={`${objectiveAssignments.length} ${objectiveAssignments.length === 1 ? t('assignment') || 'asignación' : t('assignments') || 'asignaciones'}`}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      onClick={() => handleToggleExpand(objective.id)}
                                      sx={{ cursor: 'pointer' }}
                                    />
                                  </>
                                )}
                                {objective.title}
                              </Box>
                            </TableCell>
                            <TableCell>{objective.description || '-'}</TableCell>
                            <TableCell>{getCategoryLabel(objective.category)}</TableCell>
                            <TableCell>
                              {defaultTargetValue > 0 ? defaultTargetValue.toLocaleString() : '-'}
                            </TableCell>
                            {canManage && (
                              <TableCell align="right">
                                <IconButton onClick={() => handleAssign(objective)} title={t('assign') || 'Asignar'}>
                                  {getIcon('add')}
                                </IconButton>
                                <IconButton onClick={() => handleEdit(objective)}>
                                  {getIcon('settings')}
                                </IconButton>
                                <IconButton onClick={() => handleDelete(objective)}>
                                  {getIcon('trash')}
                                </IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                          {isExpanded && objectiveAssignments.length > 0 && (
                            <TableRow>
                              <TableCell colSpan={canManage ? 5 : 4} sx={{ py: 2, bgcolor: 'background.default' }}>
                                <Box sx={{ pl: 4 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    {t('assignments') || 'Asignaciones'}:
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {(() => {
                                      // Agrupar asignaciones por mes/año
                                      const monthNames = [
                                        t('january') || 'Enero', t('february') || 'Febrero', t('march') || 'Marzo',
                                        t('april') || 'Abril', t('may') || 'Mayo', t('june') || 'Junio',
                                        t('july') || 'Julio', t('august') || 'Agosto', t('september') || 'Septiembre',
                                        t('october') || 'Octubre', t('november') || 'Noviembre', t('december') || 'Diciembre'
                                      ];
                                      
                                      const grouped = objectiveAssignments.reduce((acc, assignment) => {
                                        const monthYear = `${assignment.month || assignment.month_number || ''}-${assignment.year || ''}`;
                                        if (!acc[monthYear]) {
                                          acc[monthYear] = [];
                                        }
                                        acc[monthYear].push(assignment);
                                        return acc;
                                      }, {});
                                      
                                      return Object.entries(grouped).map(([monthYear, assignments]) => {
                                        const [month, year] = monthYear.split('-');
                                        const monthName = monthNames[parseInt(month, 10) - 1] || month;
                                        return (
                                          <Box key={monthYear} sx={{ mb: 1 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                              {monthName} {year}
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pl: 2 }}>
                                              {assignments.map((assignment) => (
                                                <Chip
                                                  key={assignment.id}
                                                  label={
                                                    assignment.scope === 'GLOBAL'
                                                      ? `🌍 ${t('global') || 'Global'}: ${assignment.target_value?.toLocaleString() || ''}`
                                                      : assignment.scope === 'BRANCH'
                                                      ? `🏢 ${assignment.branch_name || t('branch') || 'Sucursal'}: ${assignment.target_value?.toLocaleString() || ''}`
                                                      : `👤 ${assignment.user_name || t('user') || 'Usuario'}: ${assignment.target_value?.toLocaleString() || ''}`
                                                  }
                                                  color="primary"
                                                  variant="outlined"
                                                  size="small"
                                                />
                                              ))}
                                            </Box>
                                          </Box>
                                        );
                                      });
                                    })()}
                                  </Box>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredObjectives.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {addModalOpen && (
        <AddEditObjectiveModal
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onSave={handleSave}
        />
      )}

      {editModalOpen && selectedObjective && (
        <AddEditObjectiveModal
          open={editModalOpen}
          objective={selectedObjective}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedObjective(null);
          }}
          onSave={handleSave}
        />
      )}

      {deleteModalOpen && selectedObjective && (
        <DeleteObjectiveModal
          open={deleteModalOpen}
          objective={selectedObjective}
          onClose={() => {
            setDeleteModalOpen(false);
            setSelectedObjective(null);
          }}
          onDelete={handleDeleteConfirm}
        />
      )}

      {assignModalOpen && selectedObjective && (
        <AssignObjectiveModal
          open={assignModalOpen}
          objective={selectedObjective}
          onClose={() => {
            setAssignModalOpen(false);
            setSelectedObjective(null);
          }}
          onSave={async (response) => {
            // Si la respuesta contiene las asignaciones, usarlas directamente
            // Si no, recargar las asignaciones del objetivo específico
            try {
              let assignmentData = null;
              
              // Verificar si la respuesta contiene las asignaciones creadas
              if (response && Array.isArray(response)) {
                // Si es un array, son las asignaciones creadas
                assignmentData = response;
              } else if (response && response.data && Array.isArray(response.data)) {
                // Si está dentro de response.data
                assignmentData = response.data;
              } else if (response && response.assignments && Array.isArray(response.assignments)) {
                // Si está en response.assignments
                assignmentData = response.assignments;
              } else {
                // Si no está en la respuesta, recargar desde el backend
                assignmentData = await ObjectivesService.getObjectiveAssignments(selectedObjective.id);
              }
              
              // Actualizar las asignaciones en el estado
              setAssignments({
                ...assignments,
                [selectedObjective.id]: assignmentData
              });
              
              // Si estaba expandido, mantenerlo expandido
              if (!expandedRows[selectedObjective.id] && assignmentData.length > 0) {
                setExpandedRows({
                  ...expandedRows,
                  [selectedObjective.id]: true
                });
              }
            } catch (error) {
              console.error('Error updating assignments:', error);
              // Si falla, intentar recargar desde el backend
              try {
                const assignmentData = await ObjectivesService.getObjectiveAssignments(selectedObjective.id);
                setAssignments({
                  ...assignments,
                  [selectedObjective.id]: assignmentData
                });
              } catch (reloadError) {
                console.error('Error reloading assignments:', reloadError);
              }
            }
            setAssignModalOpen(false);
            setSelectedObjective(null);
          }}
        />
      )}
    </>
  );
};

export default ObjectivesTable;

